import { useTranslation } from "react-i18next";
import { stdObject } from "../../../data/types/stdObject";
import WriteInput from "../WriteInput";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { colors } from "../../../data/colors";
import { commBaseOptions, staffNames } from "../../../data/main";
import BasicButton from "../../BasicButton";
import { useMemo, useRef, useState } from "react";
import { objectToArray } from "../../../utils/objectToArray";
import { useMainContext } from "../../../context/MainContext";
import { updateItemCommissions } from "../../../screens/MainFrame/functions/commissions/updateItemCommissions";

export default function Commissions({ data = {} }: { data?: stdObject }) {
  const { t } = useTranslation();
  const { windowValues2, setWindowValues2 } = useMainContext();

  const [selValues, setSelValues] = useState({
    main: 0,
    person: 0,
    base: 0,
    value: 0,
  });
  const namesList = useRef<HTMLInputElement | null>(null);
  const usersListData: stdObject = useSelector(
    (state: RootState) => state.data.usersList,
  );
  const usersList: stdObject = useMemo(
    () => objectToArray("CODUSU", usersListData),
    [usersListData],
  );

  const writeOptions = () => {
    let optionsList = usersList
      .filter(
        (e: stdObject) =>
          e.ACTIVO &&
          e.TEC > 0 &&
          !data.value.some(
            (i: stdObject) =>
              selValues.main === i.GRUCOZ && i.AGECOZ === Number(e.CODUSU),
          ),
      )
      .map((e: stdObject) => {
        return { label: e.NOMUSU, value: e.CODUSU };
      });
    if (
      !data.value.some(
        (e: stdObject) => e.GRUCOZ === selValues.main && e.AGECOZ === 0,
      )
    ) {
      optionsList.unshift({ label: t("p.all"), value: 0 });
    }
    return optionsList;
  };

  const valueActive = () =>
    namesList?.current &&
    Number(namesList.current.value) > -1 &&
    selValues.value !== 0;

  return (
    <div>
      <table width="100%" border={0} cellPadding={3} cellSpacing={0}>
        <tbody>
          <tr>
            <td colSpan={3}>
              <WriteInput
                data={{
                  value: selValues.main,
                  type: "select",
                  options: staffNames.map((e: string, index: number) => {
                    return { label: t(e), value: index };
                  }),
                  onChange: (v: { target: { value: string } }) => {
                    setSelValues({
                      ...selValues,
                      main: Number(v.target.value),
                    });
                  },
                }}
              />
            </td>
          </tr>
          <tr>
            <td>
              <WriteInput
                data={{
                  refId: namesList,
                  value: selValues.person,
                  type: "select",
                  options: writeOptions(),
                  onChange: (v: { target: { value: string } }) => {
                    setSelValues({
                      ...selValues,
                      person: Number(v.target.value),
                    });
                  },
                }}
              />
            </td>
            <td>
              <WriteInput
                data={{
                  refId: namesList,
                  value: selValues.base,
                  type: "select",
                  options: [
                    { label: t("p.price"), value: 0 },
                    { label: t("p.profitability"), value: 1 },
                  ],
                  onChange: (v: { target: { value: string } }) => {
                    setSelValues({
                      ...selValues,
                      base: Number(v.target.value),
                    });
                  },
                }}
              />
            </td>
            <td>
              <WriteInput
                data={{
                  value: selValues.value,
                  style: { width: 50 },
                  type: "number",
                  onChange: (v: { target: { value: string } }) => {
                    setSelValues({
                      ...selValues,
                      value: Number(v.target.value),
                    });
                  },
                }}
              />
            </td>
            <td style={{ width: 20 }}>
              <BasicButton
                title={t("p.add")}
                color={valueActive() ? colors.green : colors.grey}
                onClick={() => {
                  if (namesList?.current && valueActive()) {
                    updateItemCommissions(
                      {
                        status: "add",
                        item: true,
                        GRUCOZ: selValues.main,
                        AGECOZ: Number(namesList.current.value),
                        BASCOZ: Number(selValues.base),
                        PORCOZ: Number(selValues.value),
                      },
                      { windowValues2, setWindowValues2 },
                    );
                  }
                }}
              />
            </td>
          </tr>
        </tbody>
      </table>
      <table width="100%" border={0} cellPadding={3} cellSpacing={0}>
        <tbody>
          {data.value.map((e: stdObject, index: number) => (
            <tr
              key={index}
              style={{
                backgroundColor:
                  index % 2 === 0 ? colors.lightGrey : colors.white,
              }}
            >
              <td style={{ fontSize: 14 }}>
                <div>{t(staffNames[e.GRUCOZ])}</div>
              </td>
              <td style={{ fontSize: 14 }}>
                {e.AGECOZ > 0
                  ? (usersList.find(
                      (v: stdObject) => Number(v.CODUSU) === e.AGECOZ,
                    )?.NOMUSU ?? "")
                  : t("p.all")}
              </td>
              <td align="right" width={100}>
                <WriteInput
                  data={{
                    code: index,
                    active: e.active,
                    value: e.BASCOZ,
                    type: "select",
                    options: commBaseOptions.map((e: stdObject) => {
                      return { label: t(e.label), value: e.value };
                    }),
                    onChange: (v: { target: { value: string } }) => {
                      updateItemCommissions(
                        {
                          status: "update",
                          index,
                          key: "BASCOZ",
                          value: Number(v.target.value),
                        },
                        { windowValues2, setWindowValues2 },
                      );
                    },
                  }}
                />
              </td>
              <td align="right" width={70}>
                <WriteInput
                  data={{
                    code: index,
                    active: e.active,
                    value: e.PORCOZ,
                    style: { width: 50, marginRight: 3 },
                    type: "number",
                    onChange: (v: { target: { value: string } }) => {
                      updateItemCommissions(
                        {
                          status: "update",
                          index,
                          key: "PORCOZ",
                          value: Number(v.target.value),
                        },
                        { windowValues2, setWindowValues2 },
                      );
                    },
                  }}
                  prefix="%"
                />
              </td>
              <td style={{ width: 20 }}>
                <DeleteForeverIcon
                  style={{ color: colors.red, cursor: "pointer" }}
                  onClick={() =>
                    updateItemCommissions(
                      {
                        status: "delete",
                        index,
                      },
                      { windowValues2, setWindowValues2 },
                    )
                  }
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
